<template>
  <mf-loading-dialog :loading="loading || $apollo.loading">
    <base-page-layout title="Resultados da consulta">
      <mf-button label="Voltar" color="marca-0-l40" icon="arrow_back" icon-position="left" text @click="goBack" />
      <v-card class="margin-bottom mt-2">
        <v-card-text>
          <consumer-header :consumer="consumer" :email="user.email" />
          <v-divider class="my-4" />
          <v-row>
            <v-col cols="12" class="py-0">
              <span>Status de ativação: </span>
              <b :style="activatedLabel.style">{{ activatedLabel.label }}</b>
            </v-col>
          </v-row>
          <v-row>
            <v-col v-if="user.is_confirmed" cols="12" class="py-0">
              <span>Data de ativação: </span>
              <span>{{ formatDate(user.confirmed_at) }}</span>
            </v-col>
            <v-col v-else cols="12" class="py-0">
              <span>Última mensagem de ativação enviada em: </span>
              <span>{{ formatDate(user.confirmation_sent_at) }}</span>
            </v-col>
          </v-row>
          <v-row v-if="disableActionButtons">
            <v-col cols="12" class="py-0">
              <mf-info-card :icon="infoCard.icon" :variant="infoCard.variant" :description="infoCard.description" />
            </v-col>
          </v-row>
          <v-row class="pt-3">
            <v-col cols="12" md="4" class="pt-0">
              <mf-tooltip bottom>
                <template #activator>
                  <mf-button
                    :disabled="disableActionButtons || !passwordRecoveryBalance"
                    label="Reenviar SMS de ativação"
                    color="marca-0-l40"
                    icon="sms"
                    icon-position="left"
                    outlined
                    block
                    @click="resendActivationLink(user, 'SMS')"
                  />
                </template>
                <span v-if="passwordRecoveryBalance">{{ passwordRecoveryBalance | localeString }} SMS de ativação restantes</span>
                <span v-else-if="!passwordRecoveryBalance">Saldo de SMS de ativação insuficiente</span>
              </mf-tooltip>
            </v-col>
            <v-col cols="12" md="4" class="pt-0">
              <mf-button
                :disabled="disableActionButtons"
                label="Reenviar E-mail de ativação"
                color="marca-0-l40"
                icon="email"
                icon-position="left"
                outlined
                block
                @click="resendActivationLink(user, 'EMAIL')"
              />
            </v-col>
            <v-col cols="12" md="4" class="pt-0">
              <mf-button :disabled="disableActionButtons" label="Ativar manualmente" icon="check" color="marca-0-l40" block @click="activateManually(user)" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import { PASSWORD_RECOVERY_BALANCE } from '@/modules/configuracoes/site-de-cadastramento/graphql'
import { localeString, getStatusLabel } from '@/config/helpers.js'
import BasePageLayout from '@/components/BasePageLayout.vue'
import { QUERY_USER } from '@/modules/clientes/consultar-ofertas/graphql/index.js'
import ConsumerHeader from '@/modules/clientes/consultar-ofertas/views/components/ConsumerHeader.vue'
import userActivation from '@/modules/clientes/gerenciamento/mixins/userActivation'
import { DateTime } from 'luxon'
export default {
  components: {
    BasePageLayout,
    ConsumerHeader,
  },
  mixins: [userActivation],
  props: {
    consumer: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: true,
    user: {},
  }),
  computed: {
    activatedLabel() {
      if (this.user.is_confirmed) {
        const status = getStatusLabel('ACTIVE')
        return { label: 'Ativado', style: `color: ${status.color}` }
      } else {
        const status = getStatusLabel('INACTIVE')
        return { label: 'Não ativado', style: `color: ${status.color}` }
      }
    },
    disableActionButtons() {
      return this.user.is_confirmed || this.consumer.isTemp
    },
    infoCard() {
      if (!this.disableActionButtons) return {}
      if (this.consumer.isTemp) {
        return {
          icon: 'warning',
          variant: 'warning',
          description: 'Não é possível ativar um pré-cadastro. Antes de ativar o cadastro o consumidor precisa realizar o cadastro completo no site ou no app.',
        }
      }
      if (this.user.is_confirmed) {
        return {
          icon: 'check',
          variant: 'success',
          description: 'O consumidor já ativou o cadastro.',
        }
      }
      return {}
    },
  },
  apollo: {
    passwordRecoveryBalance: {
      query: PASSWORD_RECOVERY_BALANCE,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      update({ passwordRecoveryBalance }) {
        return parseInt(passwordRecoveryBalance) || 0
      },
    },
  },
  mounted() {
    if (typeof this.consumer !== 'object') {
      this.goBack()
    } else {
      this.getData()
    }
  },
  methods: {
    localeString,
    reloadDataAfterActivation() {
      this.getData()
    },
    formatDate(date) {
      if (!date) return '-'
      return DateTime.fromISO(date).toFormat('dd/MM/yyyy HH:mm:ss')
    },
    goBack() {
      this.$router.push({ name: 'consultar-ativacao-cadastro' })
    },
    mountQuery(query) {
      return this.$apollo.query({
        query: query,
        variables: {
          _id: this.consumer.id,
        },
        fetchPolicy: 'no-cache',
      })
    },
    async getData() {
      this.loading = true
      try {
        const { data } = await this.mountQuery(QUERY_USER)
        // NO user means that the user is pre-register only
        if (data.user) {
          this.user = data.user
        } else {
          this.user = {
            _id: this.consumer.id,
            phone: this.consumer.phone,
            email: '',
            is_confirmed: false,
            confirmed_at: null,
            confirmation_sent_at: null,
          }
        }
      } catch (err) {
        console.log(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.margin-bottom {
  margin-bottom: 74px;
}

.bold-16px {
  font-size: 16px;
  font-weight: 700;
}

.bold-22px {
  font-size: 22px;
  font-weight: 700;
}

.normal-14px {
  font-size: 14px;
  font-weight: 400;
}

.color-marca-0-l10 {
  color: $marca-0-l10;
}

.color-marca-0-l30 {
  color: $marca-0-l30;
}

.color-orange {
  color: #f36600;
}

.color-cinza-0 {
  color: $cinza-0;
}
</style>
